import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MultiSelect, Popover } from '@mantine/core';
import { type DatesRangeValue } from '@mantine/dates';
import '@mantine/dates/styles.css';
import { IoChevronDown } from 'react-icons/io5';
import { OrderStatus, getStatusLabel } from '@modules/orders';
import { sellersApi } from '@modules/sellers/api/sellers.api';
import { pickupPointsApi } from '@modules/pickup-points/api/pickup-points.api';
import { FinanceDateRangeInput } from '@repo/date-range-picker';

const STATUS_ORDER: OrderStatus[] = [
  OrderStatus.PENDING,
  OrderStatus.CONFIRMED,
  OrderStatus.IN_PROGRESS,
  OrderStatus.READY,
  OrderStatus.IN_DELIVERY,
  OrderStatus.DELIVERED,
  OrderStatus.CANCELED,
];

export type OrdersDeliveryFilter = 'PICKUP' | 'DOSTAVISTA' | 'SELLER';

const DELIVERY_OPTIONS: { value: OrdersDeliveryFilter; label: string }[] = [
  { value: 'PICKUP', label: 'Самовывоз' },
  { value: 'DOSTAVISTA', label: 'Достависта' },
  { value: 'SELLER', label: 'Своими силами' },
];

const DELIVERY_VALUES = new Set<string>(DELIVERY_OPTIONS.map((option) => option.value));

export type OrdersPageFilters = {
  statuses: OrderStatus[];
  cities: string[];
  sellerIds: number[];
  shopNames: string[];
  dateFrom: string;
  dateTo: string;
  delivery: OrdersDeliveryFilter[];
};

export const EMPTY_ORDERS_FILTERS: OrdersPageFilters = {
  statuses: [],
  cities: [],
  sellerIds: [],
  shopNames: [],
  dateFrom: '',
  dateTo: '',
  delivery: [],
};

const ORDERS_FILTERS_STORAGE_KEY = 'admin-orders-filters';

const STATUS_VALUES = new Set<string>(Object.values(OrderStatus));

function isYmd(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function readStoredOrdersFilters(): OrdersPageFilters {
  try {
    const raw = localStorage.getItem(ORDERS_FILTERS_STORAGE_KEY);
    if (!raw) return EMPTY_ORDERS_FILTERS;
    const parsed = JSON.parse(raw) as Partial<OrdersPageFilters>;
    const statuses = Array.isArray(parsed.statuses)
      ? parsed.statuses.filter(
          (status): status is OrderStatus => typeof status === 'string' && STATUS_VALUES.has(status),
        )
      : [];
    const cities = Array.isArray(parsed.cities)
      ? parsed.cities
          .filter((city): city is string => typeof city === 'string' && city.trim() !== '')
          .map((city) => city.trim())
      : [];
    const sellerIds = Array.isArray(parsed.sellerIds)
      ? parsed.sellerIds.filter((id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0)
      : [];
    const storedNames = Array.isArray(parsed.shopNames) ? parsed.shopNames : [];
    const storedDelivery = Array.isArray(parsed.delivery)
      ? parsed.delivery
      : typeof parsed.delivery === 'string'
        ? [parsed.delivery]
        : [];
    const delivery = [
      ...new Set(
        storedDelivery.filter(
          (item): item is OrdersDeliveryFilter => typeof item === 'string' && DELIVERY_VALUES.has(item),
        ),
      ),
    ];
    let dateFrom = isYmd(parsed.dateFrom) ? parsed.dateFrom : '';
    let dateTo = isYmd(parsed.dateTo) ? parsed.dateTo : '';
    if (dateFrom && dateTo && dateFrom > dateTo) {
      [dateFrom, dateTo] = [dateTo, dateFrom];
    }
    return {
      statuses,
      cities,
      sellerIds,
      shopNames: sellerIds.map((_, index) =>
        typeof storedNames[index] === 'string' ? storedNames[index] : '',
      ),
      dateFrom,
      dateTo,
      delivery,
    };
  } catch {
    return EMPTY_ORDERS_FILTERS;
  }
}

export function writeStoredOrdersFilters(filters: OrdersPageFilters) {
  localStorage.setItem(ORDERS_FILTERS_STORAGE_KEY, JSON.stringify(filters));
}

type OrdersFiltersPopoverProps = {
  value: OrdersPageFilters;
  selectedCount: number;
  onChange: (filters: OrdersPageFilters) => void;
};

function toYmd(value: Date | string | null): string {
  if (!value) return '';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function fromYmd(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export default function OrdersFiltersPopover({
  value,
  selectedCount,
  onChange,
}: OrdersFiltersPopoverProps) {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!opened) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest(
          '.orders-page__filters-trigger, .orders-filters-popover, .mantine-Popover-dropdown, .mantine-Combobox-dropdown',
        )
      ) {
        return;
      }
      setOpened(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [opened]);

  const sellersQuery = useQuery({
    queryKey: ['sellers'],
    queryFn: () => sellersApi.getAll(),
    enabled: opened,
  });
  const pointsQuery = useQuery({
    queryKey: ['pickup-points-managed'],
    queryFn: () => pickupPointsApi.getAll(),
    enabled: opened,
  });

  const points = pointsQuery.data ?? [];
  const cities = useMemo(() => {
    const names = new Set(points.map((point) => point.city.trim()).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [points]);

  const shops = useMemo(() => {
    const sellers = sellersQuery.data ?? [];
    const sellerIdsInCities =
      value.cities.length > 0
        ? new Set(
            points
              .filter(
                (point) => value.cities.includes(point.city.trim()) && point.sellerId != null,
              )
              .map((point) => point.sellerId as number),
          )
        : null;
    return sellers
      .filter((seller) => sellerIdsInCities == null || sellerIdsInCities.has(seller.id))
      .slice()
      .sort((a, b) => a.shopName.localeCompare(b.shopName, 'ru'));
  }, [sellersQuery.data, points, value.cities]);

  const rangeValue: DatesRangeValue = [fromYmd(value.dateFrom), fromYmd(value.dateTo)];

  const setCities = (cities: string[]) => {
    if (cities.length === 0 || value.sellerIds.length === 0) {
      onChange({ ...value, cities });
      return;
    }
    const allowed = new Set(
      points
        .filter((point) => cities.includes(point.city.trim()) && point.sellerId != null)
        .map((point) => point.sellerId as number),
    );
    const sellerIds = value.sellerIds.filter((id) => allowed.has(id));
    const shopNames = sellerIds.map((id) => value.shopNames[value.sellerIds.indexOf(id)] ?? '');
    onChange({ ...value, cities, sellerIds, shopNames });
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      offset={8}
      withinPortal
      closeOnClickOutside={false}
      trapFocus={false}
      classNames={{ dropdown: 'orders-filters-popover' }}
    >
      <Popover.Target>
        <button
          type="button"
          className={`orders-page__filters-trigger${selectedCount > 0 ? ' orders-page__filters-trigger--active' : ''}${opened ? ' orders-page__filters-trigger--open' : ''}`}
          aria-label="Фильтры заказов"
          aria-expanded={opened}
          onClick={() => setOpened((current) => !current)}
        >
          <span className="orders-page__filters-trigger-label">Фильтры</span>
          {selectedCount > 0 ? (
            <span className="orders-page__filters-badge">{selectedCount}</span>
          ) : null}
          <IoChevronDown size={14} aria-hidden />
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <div className="orders-filters">
          <div className="orders-filters__header">
            <button
              type="button"
              className="orders-filters__reset"
              disabled={selectedCount === 0}
              onClick={() => onChange(EMPTY_ORDERS_FILTERS)}
            >
              Сбросить все
            </button>
          </div>
          <MultiSelect
            className="multiselect"
            label="Город"
            size="xs"
            placeholder={value.cities.length > 0 ? undefined : 'Все города'}
            searchable
            clearable
            data={cities.map((city) => ({ value: city, label: city }))}
            value={value.cities}
            onChange={setCities}
            nothingFoundMessage="Города не найдены"
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect"
            label="Магазин"
            size="xs"
            placeholder={value.sellerIds.length > 0 ? undefined : 'Все магазины'}
            searchable
            clearable
            data={shops.map((seller) => ({ value: String(seller.id), label: seller.shopName }))}
            value={value.sellerIds.map(String)}
            onChange={(values) => {
              const sellerIds = values.map(Number).filter((id) => Number.isInteger(id));
              const shopNames = sellerIds.map(
                (id) => shops.find((seller) => seller.id === id)?.shopName ?? '',
              );
              onChange({ ...value, sellerIds, shopNames });
            }}
            nothingFoundMessage="Магазины не найдены"
            comboboxProps={{ zIndex: 1100 }}
          />
          <MultiSelect
            className="multiselect"
            label="Статус"
            size="xs"
            placeholder={value.statuses.length > 0 ? undefined : 'Все статусы'}
            clearable
            data={STATUS_ORDER.map((status) => ({ value: status, label: getStatusLabel(status) }))}
            value={value.statuses}
            onChange={(values) => onChange({ ...value, statuses: values as OrderStatus[] })}
            comboboxProps={{ zIndex: 1100 }}
          />
          <FinanceDateRangeInput
            label="Дата слота"
            value={rangeValue}
            onChange={(range) => {
              const [from, to] = range;
              let dateFrom = toYmd(from);
              let dateTo = toYmd(to);
              if (dateFrom && dateTo && dateFrom > dateTo) {
                [dateFrom, dateTo] = [dateTo, dateFrom];
              }
              onChange({ ...value, dateFrom, dateTo });
            }}
            valueFormat="DD.MM.YYYY"
            placeholder="Выберите даты"
            size="xs"
            radius="md"
            style={{ width: '100%' }}
            popoverProps={{ zIndex: 400, withinPortal: true }}
          />
          <MultiSelect
            className="multiselect"
            label="Получение"
            size="xs"
            placeholder={value.delivery.length > 0 ? undefined : 'Все'}
            clearable
            data={DELIVERY_OPTIONS}
            value={value.delivery}
            onChange={(values) =>
              onChange({
                ...value,
                delivery: values.filter(
                  (item): item is OrdersDeliveryFilter => DELIVERY_VALUES.has(item),
                ),
              })
            }
            comboboxProps={{ zIndex: 1100 }}
          />
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
